import { AlarmOn, Favorite, MoreVert } from '@mui/icons-material';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, type Theme } from '@mui/material';
import { type SystemStyleObject } from '@mui/system';
import { type MovieStatus, type ScreenType, type TvStatus } from '@tmdb-graphql-api/schema/types';
import { type JSX, type MouseEvent, useState } from 'react';
import { mapScreenTypeStatusToWatchlistCategory } from '#helpers/enumMappers.ts';
import { useStore } from '#store/useStore.ts';

export type MoreOptionButtonProps = {
  dropShadow?: boolean;
  id: string;
  renderAdditionalOptionItems?: () => JSX.Element | undefined;
  status: MovieStatus | TvStatus;
  sx?: SystemStyleObject<Theme>;
  type: ScreenType;
};

export const MoreOptionButton = ({
  dropShadow,
  id,
  renderAdditionalOptionItems,
  status,
  sx,
  type,
}: MoreOptionButtonProps) => {
  const [anchorElement, setAnchorElement] = useState<HTMLElement>();
  const open = Boolean(anchorElement);
  const addFavorite = useStore(store => store.addFavorite);
  const hasFavorite = useStore(store => store.hasFavorite);
  const removeFavorite = useStore(store => store.removeFavorite);
  const addWatchlistItem = useStore(store => store.addWatchlistItem);
  const hasWatchlistItem = useStore(store => store.hasWatchlistItem);
  const removeWatchlistItem = useStore(store => store.removeWatchlistItem);
  const watchlistCategory = mapScreenTypeStatusToWatchlistCategory(status);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(undefined);
  };

  const handleFavorites = () => {
    handleClose();

    setTimeout(() => {
      if (hasFavorite({ id, type })) {
        removeFavorite({ id, type });
      } else {
        addFavorite({ id, type });
      }
    }, 100);
  };

  const handleWatchlist = () => {
    handleClose();

    setTimeout(() => {
      if (hasWatchlistItem({ category: watchlistCategory, id, type })) {
        removeWatchlistItem({ category: watchlistCategory, id, type });
      } else {
        addWatchlistItem({ category: watchlistCategory, id, type });
      }
    }, 100);
  };

  const favoriteText = hasFavorite({ id, type }) ? 'Remove from favorites' : 'Add to favorites';

  const watchlistText = hasWatchlistItem({ category: watchlistCategory, id, type })
    ? 'Remove from watchlist'
    : 'Add to watchlist';

  return (
    <div>
      <IconButton
        aria-controls="more-options-menu"
        aria-label="Show more options"
        className="moreOptionsButton"
        edge="start"
        id={`more-options-menu-${id}`}
        onClick={handleClick}
        sx={{
          ...sx,
        }}
      >
        <MoreVert sx={{ filter: dropShadow ? 'drop-shadow(0px 1px 1px rgb(0 0 0 / 0.8))' : undefined }} />
      </IconButton>
      <Menu
        anchorEl={anchorElement}
        id="more-options-menu"
        onClose={handleClose}
        open={open}
        slotProps={{
          list: {
            'aria-labelledby': `more-options-menu-${id}`,
          },
        }}
      >
        <MenuItem onClick={handleFavorites}>
          <ListItemIcon>
            <Favorite fontSize="small" />
          </ListItemIcon>
          <ListItemText>{favoriteText}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleWatchlist}>
          <ListItemIcon>
            <AlarmOn fontSize="small" />
          </ListItemIcon>
          <ListItemText>{watchlistText}</ListItemText>
        </MenuItem>
        {renderAdditionalOptionItems ? renderAdditionalOptionItems() : undefined}
      </Menu>
    </div>
  );
};
